import React, { useMemo } from 'react'
import {
  Box,
  Card,
  CardContent,
  CircularProgress,
  Divider,
  LinearProgress,
  Stack,
  Typography,
} from '@mui/material'
import { DateTime } from 'luxon'
import dayjs from 'dayjs'
import { PrivateUserInfoType, Vehicle } from '@otw/models'
import { useAuth } from 'contexts/AuthContext'
import { useDashboardPurchasedForInventory, useDashboardSoldQuery } from 'hooks/dashboard'
import { useQuotas } from '@/hooks/admin/useQuotas'
import { useTimesheetHoursThisWeek } from '@/hooks/timesheet/useTimesheetHoursThisWeek'
import { useUsers, getUserDisplayName } from 'hooks/useUsers'
import { formatDurationHoursMinutes } from '@/core/timesheet/utils/utils'
import {
  getDepartmentLabel,
  getDepartmentLeaderRole,
  getDepartmentPeersForLeader,
  getDepartmentQuotaType,
  getUserDepartment,
} from 'views/SuperAdmin/SuperAdminHome/components/tabs/PayrollTab/utils/departments'
import {
  getQuotaProgressLabel,
  getVehiclesForQuota,
  sortQuotaVehicles,
} from './quotaProgress'
import { QuotaProgressRow } from './QuotaProgressRow'
import { QuotaVehicleList } from './QuotaVehicleList'

/**
 * Displays the current user's quota and progress at the top of their dashboard.
 * Department managers/directors also see teammates' quotas and trucks in the same department.
 */
export const DashboardQuotaBanner: React.FC = () => {
  const { userInfo } = useAuth()
  const { users } = useUsers()
  const monthRange = useMemo(
    () => ({
      start: dayjs().startOf('month').toDate(),
      end: dayjs().endOf('day').toDate(),
    }),
    [],
  )
  const { data: soldThisMonth } = useDashboardSoldQuery(monthRange)
  const { data: purchasedForInventory } = useDashboardPurchasedForInventory()
  const vehicles = useMemo(() => {
    const byId = new Map<string, Vehicle>()
    for (const vehicle of soldThisMonth ?? []) byId.set(vehicle.id, vehicle)
    for (const vehicle of purchasedForInventory ?? []) byId.set(vehicle.id, vehicle)
    return [...byId.values()]
  }, [soldThisMonth, purchasedForInventory])

  const leaderRole = userInfo ? getDepartmentLeaderRole(userInfo) : null
  const leaderDepartment = userInfo ? getUserDepartment(userInfo) : null
  const departmentQuotaType = leaderDepartment ? getDepartmentQuotaType(leaderDepartment) : null

  const departmentPeers = useMemo(() => {
    if (!userInfo || !leaderRole) return [] as PrivateUserInfoType[]
    return getDepartmentPeersForLeader(userInfo, users).sort((a, b) =>
      getUserDisplayName(a).localeCompare(getUserDisplayName(b)),
    )
  }, [userInfo, leaderRole, users])

  const peerIds = useMemo(() => departmentPeers.map((peer) => peer.id), [departmentPeers])

  const ownQuotaUserIds = useMemo(() => (userInfo?.id ? [userInfo.id] : []), [userInfo?.id])
  const { quotas: inventoryQuotas } = useQuotas(ownQuotaUserIds, 'inventory_trucks')
  const { quotas: salesQuotas } = useQuotas(ownQuotaUserIds, 'vehicles_sold')
  const { quotas: teamQuotas, isLoading: isLoadingTeamQuotas } = useQuotas(
    departmentQuotaType ? peerIds : [],
    departmentQuotaType ?? 'vehicles_sold',
  )

  const {
    hoursThisWeekSeconds,
    hoursGoal,
    hasTrackedHoursThisWeek,
    isLoading: isLoadingHours,
  } = useTimesheetHoursThisWeek()

  const inventoryQuota = userInfo?.id ? inventoryQuotas[userInfo.id]?.currentQuota ?? null : null
  const salesQuota = userInfo?.id ? salesQuotas[userInfo.id]?.currentQuota ?? null : null

  const monthBounds = useMemo(() => {
    const now = DateTime.now()
    return { start: now.startOf('month'), end: now.endOf('month') }
  }, [])

  const purchasedInventoryTrucks = useMemo(() => {
    if (!userInfo || !vehicles || inventoryQuota == null) return [] as Vehicle[]
    return sortQuotaVehicles(
      'inventory_trucks',
      getVehiclesForQuota('inventory_trucks', userInfo, vehicles, monthBounds.start, monthBounds.end),
    )
  }, [userInfo, vehicles, inventoryQuota, monthBounds])

  const soldVehicles = useMemo(() => {
    if (!userInfo || !vehicles || salesQuota == null) return [] as Vehicle[]
    return sortQuotaVehicles(
      'vehicles_sold',
      getVehiclesForQuota('vehicles_sold', userInfo, vehicles, monthBounds.start, monthBounds.end),
    )
  }, [userInfo, vehicles, salesQuota, monthBounds])

  const teamQuotaRows = useMemo(() => {
    if (!departmentQuotaType || !vehicles) return []

    const label = getQuotaProgressLabel(departmentQuotaType)

    return departmentPeers
      .map((peer) => {
        const quota = teamQuotas[peer.id]?.currentQuota ?? null
        if (quota == null) return null
        const peerVehicles = sortQuotaVehicles(
          departmentQuotaType,
          getVehiclesForQuota(
            departmentQuotaType,
            peer,
            vehicles,
            monthBounds.start,
            monthBounds.end,
          ),
        )
        return {
          peerId: peer.id,
          name: getUserDisplayName(peer),
          label,
          current: peerVehicles.length,
          quota,
          vehicles: peerVehicles,
        }
      })
      .filter((row): row is NonNullable<typeof row> => row !== null)
  }, [departmentQuotaType, departmentPeers, teamQuotas, vehicles, monthBounds])

  const hasOwnQuota =
    inventoryQuota != null || salesQuota != null || (hasTrackedHoursThisWeek && !isLoadingHours)
  const showTeamSection =
    leaderRole != null &&
    leaderDepartment != null &&
    departmentQuotaType != null &&
    departmentPeers.length > 0

  if (!hasOwnQuota && !showTeamSection) return null

  const hoursPercent =
    hoursGoal > 0 ? Math.min(100, (hoursThisWeekSeconds / 3600 / hoursGoal) * 100) : 0
  const hoursComplete = hasTrackedHoursThisWeek && hoursThisWeekSeconds >= hoursGoal * 3600

  return (
    <Card
      variant="outlined"
      sx={{
        width: '100%',
        mb: 2,
        borderColor: 'divider',
        '&:hover': { borderColor: 'primary.light', boxShadow: 1 },
        transition: 'border-color 0.2s, box-shadow 0.2s',
      }}
    >
      <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
        {hasOwnQuota && (
          <>
            <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1.5 }}>
              My quota this month ({dayjs().format('MMMM YYYY')})
            </Typography>
            <Stack spacing={2}>
              {inventoryQuota != null && (
                <Box>
                  <QuotaProgressRow
                    label="Trucks purchased for inventory"
                    current={purchasedInventoryTrucks.length}
                    quota={inventoryQuota}
                  />
                  <QuotaVehicleList vehicles={purchasedInventoryTrucks} />
                </Box>
              )}
              {salesQuota != null && (
                <Box>
                  <QuotaProgressRow
                    label="Vehicles sold"
                    current={soldVehicles.length}
                    quota={salesQuota}
                  />
                  <QuotaVehicleList vehicles={soldVehicles} />
                </Box>
              )}
              {hasTrackedHoursThisWeek && !isLoadingHours && (
                <Box>
                  <Box
                    sx={{
                      display: 'flex',
                      flexWrap: 'wrap',
                      alignItems: 'baseline',
                      justifyContent: 'space-between',
                      gap: 1,
                      mb: 0.5,
                    }}
                  >
                    <Typography variant="body2">
                      Hours this week:{' '}
                      <Typography component="span" variant="body2" fontWeight={600} color="primary.main">
                        {formatDurationHoursMinutes(hoursThisWeekSeconds)}
                      </Typography>
                      {' / '}
                      {hoursGoal}h goal
                    </Typography>
                    {hoursComplete && (
                      <Typography variant="caption" color="success.main">
                        {hoursGoal}h reached
                      </Typography>
                    )}
                  </Box>
                  <LinearProgress
                    variant="determinate"
                    value={hoursPercent}
                    color={hoursComplete ? 'success' : 'primary'}
                    sx={{
                      height: 8,
                      borderRadius: 1,
                      backgroundColor: 'action.hover',
                      '& .MuiLinearProgress-bar': { borderRadius: 1 },
                    }}
                  />
                </Box>
              )}
            </Stack>
          </>
        )}

        {showTeamSection && (
          <>
            {hasOwnQuota && <Divider sx={{ my: 2 }} />}
            <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1.5 }}>
              Team quota — {getDepartmentLabel(leaderDepartment)}
            </Typography>
            {isLoadingTeamQuotas ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
                <CircularProgress size={24} />
              </Box>
            ) : teamQuotaRows.length === 0 ? (
              <Typography variant="body2" color="text.secondary">
                No teammates have a {getQuotaProgressLabel(departmentQuotaType).toLowerCase()} quota
                set this month.
              </Typography>
            ) : (
              <Stack spacing={2}>
                {teamQuotaRows.map((row) => (
                  <Box key={row.peerId}>
                    <Typography variant="body2" fontWeight={600} sx={{ mb: 0.75 }}>
                      {row.name}
                    </Typography>
                    <QuotaProgressRow label={row.label} current={row.current} quota={row.quota} />
                    <QuotaVehicleList vehicles={row.vehicles} />
                  </Box>
                ))}
              </Stack>
            )}
          </>
        )}
      </CardContent>
    </Card>
  )
}

export default DashboardQuotaBanner
